import { useState, useRef, useEffect } from "react";
import koddiLogo from "./koddilogoblue327ffb.png";
import { step1, step2, step3, step4, step5, step6, step7, step8 } from "./payloads";

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const API_BASE = ""; // Cloudflare Pages Function handles /auction-engine/* routing
const DISPLAY_BASE = "https://usfoods.koddi.io"; // shown in UI — update to match config.js

// ─── SCENARIO DEFINITIONS ─────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: "basic", title: "Basic Ad Request / Response",
    steps: [
      { id: 1, title: "Simple Sponsored Search Request",
        description: "A basic sponsored search auction across multiple brands. This shows the fundamental request shape and how winners are returned.",
        endpoint: "/auction-engine/winning_ads", method: "POST",
        buildRequest: () => step1.request, buildText: () => step1.text, runLabel: "Run Auction" },
      { id: 2, title: "Single Winner — One Result",
        description: "Reducing max_requested to 1 means only the top bidder wins. Watch how the response changes with a single winner returned.",
        endpoint: "/auction-engine/winning_ads", method: "POST",
        buildRequest: () => step2.request, buildText: () => step2.text, runLabel: "Run Auction" },
    ],
  },
  {
    id: "relevance", title: "Publisher Score / Relevance Boosting",
    steps: [
      { id: 3, title: "Equal Relevance — Fair Auction",
        description: "All bidders have publisher_score: 1. With equal relevance, the winner is determined purely by bid price.",
        endpoint: "/auction-engine/winning_ads", method: "POST",
        buildRequest: () => step3.request, buildText: () => step3.text, runLabel: "Run Auction" },
      { id: 4, title: "Boost Relevance — Publisher Score Advantage",
        description: "Kellogg and Kraft Heinz receive a publisher_score of 2, doubling their effective bid. Even lower-bidding advertisers can win with a relevance edge.",
        endpoint: "/auction-engine/winning_ads", method: "POST",
        buildRequest: () => step4.request, buildText: () => step4.text, runLabel: "Run Auction" },
    ],
  },
  {
    id: "targeting", title: "Targeting / Filters",
    steps: [
      { id: 5, title: "Device Type Targeting",
        description: "Pass device_type in the targeting block to simulate a desktop user. Advertisers with device-specific targeting will respond accordingly.",
        endpoint: "/auction-engine/winning_ads", method: "POST",
        buildRequest: () => step5.request, buildText: () => step5.text, runLabel: "Run Auction" },
      { id: 6, title: "Division Targeting",
        description: "Pass a division ID in the targeting block to simulate a user in a specific distribution division. Only advertisers targeting that division will compete.",
        endpoint: "/auction-engine/winning_ads", method: "POST",
        buildRequest: () => step6.request, buildText: () => step6.text, runLabel: "Run Auction" },
      { id: 7, title: "Keyword Targeting",
        description: "One of the Hershey\'s campaigns is boosting bids for the keyword \'chocolate\'. Run both auctions side-by-side to see how passing that keyword in the targeting block changes the winning bids.",
        endpoint: "/auction-engine/winning_ads", method: "POST",
        isSideBySide: true, labelA: "No Keyword", labelB: 'keyword: "chocolate"',
        buildRequest: (variant) => variant === "A" ? step7.requestA : step7.requestB,
        buildText: (variant) => variant === "A" ? step7.textA : step7.textB,
        runLabel: "Run Both" },
    ],
  },
  {
    id: "price_floor", title: "Price Floors / Min Bids",
    steps: [
      { id: 8, title: "Price Floor Comparison",
        description: "Run both auctions side-by-side — one without a floor and one with minimum_bid_micros set — to see how the floor filters out low-bidding advertisers.",
        endpoint: "/auction-engine/winning_ads", method: "POST",
        isSideBySide: true, labelA: "No Floor", labelB: "minimum_bid_micros: 500000",
        editableParams: [{ key: "priceFloor", label: "Price Floor ($)", defaultValue: "0.50" }],
        buildRequest: (variant, params) => {
          if (variant === "A") return step8.requestA;
          const micros = Math.round(parseFloat((params && params.priceFloor) || "0.50") * 1000000);
          return { ...step8.requestB, minimum_bid_micros: micros };
        },
        buildText: (variant, params) => {
          if (variant === "A") return step8.textA;
          const micros = Math.round(parseFloat((params && params.priceFloor) || "0.50") * 1000000);
          return step8.textB.replace(/"minimum_bid_micros": \d+/, `"minimum_bid_micros": ${micros}`);
        },
        runLabel: "Run Both" },
    ],
  },
];

const ALL_STEPS = SECTIONS.flatMap((s) => s.steps);
const TOTAL_STEPS = ALL_STEPS.length;

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function stepIndex(stepId) {
  return ALL_STEPS.findIndex((s) => s.id === stepId);
}

async function callAPI(endpoint, body) {
  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  return res.json();
}

// ─── JSON RENDERER ────────────────────────────────────────────────────────────
function JsonNode({ data, depth = 0 }) {
  const [collapsed, setCollapsed] = useState(false);
  const indent = depth * 16;

  if (data === null) return <span style={{ color: "#f97316" }}>null</span>;
  if (typeof data === "boolean")
    return <span style={{ color: "#f97316" }}>{String(data)}</span>;
  if (typeof data === "number")
    return <span style={{ color: "#60a5fa" }}>{data}</span>;
  if (typeof data === "string")
    return <span style={{ color: "#86efac" }}>"{data}"</span>;

  if (Array.isArray(data)) {
    if (data.length === 0) return <span style={{ color: "#e5e7eb" }}>[]</span>;
    return (
      <span>
        <button
          onClick={() => setCollapsed(!collapsed)}
          style={{
            background: "none",
            border: "none",
            color: "#3b82f6",
            cursor: "pointer",
            padding: 0,
            fontSize: 11,
            marginRight: 4,
          }}
        >
          {collapsed ? "▶" : "▼"}
        </button>
        <span style={{ color: "#e5e7eb" }}>[</span>
        {collapsed ? (
          <span
            style={{ color: "#6b7280", cursor: "pointer" }}
            onClick={() => setCollapsed(false)}
          >
            {" "}
            {data.length} items{" "}
          </span>
        ) : (
          <>
            {data.map((item, i) => (
              <div key={i} style={{ paddingLeft: indent + 16 }}>
                <JsonNode data={item} depth={depth + 1} />
                {i < data.length - 1 && (
                  <span style={{ color: "#6b7280" }}>,</span>
                )}
              </div>
            ))}
            <div style={{ paddingLeft: indent }}>
              <span style={{ color: "#e5e7eb" }}>]</span>
            </div>
          </>
        )}
      </span>
    );
  }

  if (typeof data === "object") {
    const keys = Object.keys(data);
    if (keys.length === 0) return <span style={{ color: "#e5e7eb" }}>{"{}"}</span>;
    return (
      <span>
        <button
          onClick={() => setCollapsed(!collapsed)}
          style={{
            background: "none",
            border: "none",
            color: "#3b82f6",
            cursor: "pointer",
            padding: 0,
            fontSize: 11,
            marginRight: 4,
          }}
        >
          {collapsed ? "▶" : "▼"}
        </button>
        <span style={{ color: "#e5e7eb" }}>{"{"}</span>
        {collapsed ? (
          <span
            style={{ color: "#6b7280", cursor: "pointer" }}
            onClick={() => setCollapsed(false)}
          >
            {" "}
            {keys.length} keys{" "}
          </span>
        ) : (
          <>
            {keys.map((key, i) => (
              <div key={key} style={{ paddingLeft: indent + 16 }}>
                <span style={{ color: "#c084fc" }}>"{key}"</span>
                <span style={{ color: "#e5e7eb" }}>: </span>
                <JsonNode data={data[key]} depth={depth + 1} />
                {i < keys.length - 1 && (
                  <span style={{ color: "#6b7280" }}>,</span>
                )}
              </div>
            ))}
            <div style={{ paddingLeft: indent }}>
              <span style={{ color: "#e5e7eb" }}>{"}"}</span>
            </div>
          </>
        )}
      </span>
    );
  }
  return <span>{String(data)}</span>;
}

function JsonViewer({ data }) {
  return (
    <div
      style={{
        fontFamily: '"JetBrains Mono", "Fira Code", monospace',
        fontSize: 13,
        lineHeight: 1.6,
        color: "#e5e7eb",
        padding: "16px",
        overflowX: "auto",
      }}
    >
      <JsonNode data={data} />
    </div>
  );
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function stripComments(str) {
  return str
    .split("\n")
    .map(line => line.replace(/\/\/.*$/, "").trimEnd())
    .join("\n")
    .replace(/,(\s*[}\]])/g, "$1");
}

// ─── COLORIZE JSON+COMMENTS TEXT ─────────────────────────────────────────────
function colorize(text) {
  return text.split("\n").map(line => {
    const ciIdx = line.indexOf("//");
    const code = ciIdx >= 0 ? line.slice(0, ciIdx) : line;
    const comment = ciIdx >= 0 ? line.slice(ciIdx) : "";

    let result = "";
    let i = 0;
    while (i < code.length) {
      if (code[i] === '"') {
        // scan to end of string
        let j = i + 1;
        while (j < code.length) {
          if (code[j] === "\\") { j += 2; continue; }
          if (code[j] === '"') { j++; break; }
          j++;
        }
        const str = code.slice(i, j);
        // peek ahead - if next non-space char is : it's a key
        let k = j;
        while (k < code.length && (code[k] === " " || code[k] === "\t")) k++;
        if (code[k] === ":") {
          result += `<span style="color:#c084fc">${str}</span>`;
        } else {
          result += `<span style="color:#86efac">${str}</span>`;
        }
        i = j;
      } else if (code[i] === "-" && /\d/.test(code[i+1] || "")) {
        let j = i + 1;
        while (j < code.length && /[\d.]/.test(code[j])) j++;
        result += `<span style="color:#60a5fa">${code.slice(i, j)}</span>`;
        i = j;
      } else if (/\d/.test(code[i])) {
        let j = i;
        while (j < code.length && /[\d.]/.test(code[j])) j++;
        result += `<span style="color:#60a5fa">${code.slice(i, j)}</span>`;
        i = j;
      } else if (code.slice(i, i+4) === "true") {
        result += `<span style="color:#60a5fa">true</span>`; i += 4;
      } else if (code.slice(i, i+5) === "false") {
        result += `<span style="color:#60a5fa">false</span>`; i += 5;
      } else if (code.slice(i, i+4) === "null") {
        result += `<span style="color:#60a5fa">null</span>`; i += 4;
      } else if ("{[]}".includes(code[i])) {
        result += `<span style="color:#e5e7eb">${code[i]}</span>`; i++;
      } else if (code[i] === ":") {
        result += `<span style="color:#e5e7eb">:</span>`; i++;
      } else if (code[i] === ",") {
        result += `<span style="color:#6b7280">,</span>`; i++;
      } else {
        result += code[i]; i++;
      }
    }

    const commentHtml = comment
      ? `<span style="color:#4b5563;font-style:italic">${comment}</span>`
      : "";

    return result + commentHtml;
  }).join("\n");
}

// ─── EDITABLE REQUEST PANEL ───────────────────────────────────────────────────
function EditableRequest({ defaultText, defaultValue, onChange }) {
  const initialText = defaultText || JSON.stringify(defaultValue, null, 2);

  // Track the last defaultText we rendered from — when it changes externally, reset
  const [controlledText, setControlledText] = useState(initialText);
  const [text, setText] = useState(initialText);
  const [parseError, setParseError] = useState(null);
  const textareaRef = useRef(null);
  const preRef = useRef(null);

  // When defaultText changes from outside (e.g. param input), sync everything
  if (defaultText && defaultText !== controlledText) {
    setControlledText(defaultText);
    setText(defaultText);
    setParseError(null);
    try { onChange(JSON.parse(stripComments(defaultText))); } catch { onChange(defaultValue); }
  }

  // Fire onChange on mount so payload is never null
  useEffect(() => {
    try { onChange(JSON.parse(stripComments(initialText))); }
    catch { onChange(defaultValue); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleChange(e) {
    const val = e.target.value;
    setText(val);
    // sync scroll
    if (preRef.current) preRef.current.scrollTop = e.target.scrollTop;
    try {
      const p = JSON.parse(stripComments(val));
      setParseError(null);
      onChange(p);
    } catch {
      setParseError("Invalid JSON — fix before running");
      onChange(null);
    }
  }

  function handleScroll(e) {
    if (preRef.current) preRef.current.scrollTop = e.target.scrollTop;
  }

  function handleReset() {
    setText(initialText);
    setParseError(null);
    try { onChange(JSON.parse(stripComments(initialText))); } catch { onChange(defaultValue); }
  }

  const highlighted = colorize(text);
  const FONT = '"JetBrains Mono", "Fira Code", monospace';
  const sharedStyle = {
    position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
    margin: 0, padding: "16px",
    fontFamily: FONT, fontSize: 13, lineHeight: 1.6,
    whiteSpace: "pre", overflowX: "auto", overflowY: "auto",
    boxSizing: "border-box",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{
        padding: "8px 16px", borderBottom: "1px solid #1f2937",
        background: "#0f172a", display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: "#6b7280", textTransform: "uppercase" }}>
          REQUEST
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {parseError && <span style={{ fontSize: 11, color: "#f87171" }}>⚠ {parseError}</span>}
          <button onClick={handleReset} style={{
            background: "none", border: "1px solid #374151", borderRadius: 4,
            color: "#9ca3af", fontSize: 11, padding: "2px 8px", cursor: "pointer", fontFamily: "inherit",
          }}>↺ Reset</button>
        </div>
      </div>

      <div style={{ position: "relative", flex: 1, minHeight: 320, background: "#111827" }}>
        {/* Syntax-highlighted layer */}
        <pre
          ref={preRef}
          aria-hidden="true"
          dangerouslySetInnerHTML={{ __html: highlighted }}
          style={{ ...sharedStyle, color: "#e5e7eb", pointerEvents: "none", overflow: "hidden" }}
        />
        {/* Transparent editable textarea on top */}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleChange}
          onScroll={handleScroll}
          spellCheck={false}
          style={{
            ...sharedStyle,
            background: "transparent",
            color: "transparent",
            caretColor: "#60a5fa",
            border: parseError ? "1px solid #ef4444" : "1px solid transparent",
            outline: "none",
            resize: "none",
            WebkitTextFillColor: "transparent",
          }}
          onFocus={e => { if (!parseError) e.target.style.border = "1px solid #3b82f6"; }}
          onBlur={e => { e.target.style.border = parseError ? "1px solid #ef4444" : "1px solid transparent"; }}
        />
      </div>
    </div>
  );
}

// ─── STEP VIEW ────────────────────────────────────────────────────────────────
function StepView({ step, globalIndex }) {
  // Editable params state (e.g. price floor)
  const [params, setParams] = useState(
    Object.fromEntries((step.editableParams || []).map(p => [p.key, p.defaultValue]))
  );

  const defaultA = step.buildRequest("A", params);
  const defaultB = step.isSideBySide ? step.buildRequest("B", params) : null;
  const defaultTextA = step.buildText ? step.buildText("A", params) : JSON.stringify(defaultA, null, 2);
  const defaultTextB = step.isSideBySide ? (step.buildText ? step.buildText("B", params) : JSON.stringify(defaultB, null, 2)) : null;

  const [payloadA, setPayloadA] = useState(defaultA);
  const [payloadB, setPayloadB] = useState(defaultB);
  const [response, setResponse] = useState(null);
  const [responseB, setResponseB] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasInvalidPayload = payloadA === null || (step.isSideBySide && payloadB === null);

  async function handleRun() {
    if (hasInvalidPayload) return;
    setLoading(true);
    setError(null);
    setResponse(null);
    setResponseB(null);
    try {
      if (step.isSideBySide) {
        const [a, b] = await Promise.all([
          callAPI(step.endpoint, payloadA),
          callAPI(step.endpoint, payloadB),
        ]);
        setResponse(a);
        setResponseB(b);
      } else {
        const r = await callAPI(step.endpoint, payloadA);
        setResponse(r);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const sectionColor = "#3b82f6";

  return (
    <div style={{ padding: "36px 40px", maxWidth: 1400 }}>
      {/* Section badge */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span
          style={{
            background: sectionColor,
            color: "#fff",
            fontWeight: 700,
            fontSize: 12,
            padding: "2px 10px",
            borderRadius: 4,
            letterSpacing: 0.5,
          }}
        >
          {globalIndex + 1}
        </span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 1.5,
            color: "#9ca3af",
            textTransform: "uppercase",
          }}
        >
          {SECTIONS.find((s) => s.steps.some((st) => st.id === step.id))?.title}
        </span>
      </div>

      <h1
        style={{
          fontSize: 28,
          fontWeight: 700,
          color: "#111827",
          margin: "0 0 10px",
          lineHeight: 1.25,
        }}
      >
        {step.title}
      </h1>
      <p style={{ color: "#6b7280", fontSize: 15, margin: "0 0 24px", lineHeight: 1.6 }}>
        {step.description}
      </p>

      {/* Endpoint */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <span
          style={{
            background: "#16a34a",
            color: "#fff",
            fontSize: 11,
            fontWeight: 700,
            padding: "3px 8px",
            borderRadius: 4,
            letterSpacing: 1,
          }}
        >
          POST
        </span>
        <code style={{ color: "#6b7280", fontSize: 13 }}>
          {DISPLAY_BASE}
          {step.endpoint}
        </code>
      </div>

      {/* Run button */}
      <button
        onClick={handleRun}
        disabled={loading || hasInvalidPayload}
        title={hasInvalidPayload ? "Fix JSON errors before running" : ""}
        style={{
          background: hasInvalidPayload ? "#9ca3af" : loading ? "#2563eb" : "#3b82f6",
          color: "#fff",
          border: "none",
          borderRadius: 6,
          padding: "10px 24px",
          fontSize: 14,
          fontWeight: 700,
          cursor: loading || hasInvalidPayload ? "not-allowed" : "pointer",
          marginBottom: 28,
          transition: "all 0.15s",
          letterSpacing: 0.3,
        }}
      >
        {loading ? "Running…" : step.runLabel}
      </button>

      {/* Editable params (e.g. price floor) */}
      {step.editableParams && step.editableParams.length > 0 && (
        <div style={{
          border: "1px solid #e5e7eb", borderRadius: 8, padding: "16px 20px",
          marginBottom: 24, background: "#f9fafb",
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: "#9ca3af", textTransform: "uppercase", marginBottom: 12 }}>
            Editable Parameters
          </div>
          {step.editableParams.map(param => (
            <div key={param.key} style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <label style={{ fontSize: 14, color: "#374151", fontWeight: 500, minWidth: 140 }}>
                {param.label}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={params[param.key]}
                onChange={e => {
                  const newParams = { ...params, [param.key]: e.target.value };
                  setParams(newParams);
                  // Live-update payloadB with new param value
                  const newB = step.buildRequest("B", newParams);
                  setPayloadB(newB);
                }}
                style={{
                  width: 160, padding: "6px 10px", fontSize: 14,
                  border: "1px solid #d1d5db", borderRadius: 6,
                  fontFamily: '"JetBrains Mono", monospace',
                  color: "#111827", background: "#fff",
                  outline: "none",
                }}
                onFocus={e => e.target.style.borderColor = "#3b82f6"}
                onBlur={e => e.target.style.borderColor = "#d1d5db"}
              />
              <button
                onClick={() => {
                  const reset = { ...params, [param.key]: param.defaultValue };
                  setParams(reset);
                  setPayloadB(step.buildRequest("B", reset));
                }}
                style={{
                  background: "none", border: "none", color: "#ef4444",
                  fontSize: 12, cursor: "pointer", padding: 0,
                }}
              >
                Reset defaults
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Side-by-side or normal panels */}
      {step.isSideBySide ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Requests row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {[
              { label: step.labelA || "Variant A", defaultVal: defaultA, defaultTxt: defaultTextA, setter: setPayloadA },
              { label: step.labelB || "Variant B", defaultVal: defaultB, defaultTxt: defaultTextB, setter: setPayloadB },
            ].map(({ label, defaultVal, defaultTxt, setter }) => (
              <div key={label} style={{ border: "1px solid #1f2937", borderRadius: 8, overflow: "hidden" }}>
                <div style={{ background: "#0a0f1a", padding: "8px 16px", borderBottom: "1px solid #1f2937" }}>
                  <span style={{ color: "#3b82f6", fontSize: 12, fontWeight: 600 }}>{label}</span>
                </div>
                <EditableRequest defaultText={defaultTxt} defaultValue={defaultVal} onChange={setter} />
              </div>
            ))}
          </div>
          {/* Responses row — only shown after run */}
          {(response || responseB || loading || error) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {[
                { label: `RESPONSE — ${step.labelA || "Variant A"}`, res: response },
                { label: `RESPONSE — ${step.labelB || "Variant B"}`, res: responseB },
              ].map(({ label, res: r }) => (
                <Panel
                  key={label}
                  label={label}
                  data={r}
                  loading={loading}
                  error={error}
                  empty={!r && !loading && !error}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div style={{ border: "1px solid #1f2937", borderRadius: 8, overflow: "hidden" }}>
            <EditableRequest defaultText={defaultTextA} defaultValue={defaultA} onChange={setPayloadA} />
          </div>
          <Panel
            label="RESPONSE"
            data={response}
            loading={loading}
            error={error}
            empty={!response && !loading && !error}
          />
        </div>
      )}
    </div>
  );
}

function Panel({ label, data, dark, loading, error, empty }) {
  return (
    <div
      style={{
        background: dark ? "#111827" : "#f9fafb",
        border: "1px solid",
        borderColor: dark ? "#1f2937" : "#e5e7eb",
        borderRadius: 8,
        minHeight: 320,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "8px 16px",
          borderBottom: "1px solid",
          borderColor: dark ? "#1f2937" : "#e5e7eb",
          background: dark ? "#0f172a" : "#f3f4f6",
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1.5,
            color: dark ? "#6b7280" : "#9ca3af",
            textTransform: "uppercase",
          }}
        >
          {label}
        </span>
      </div>

      {dark ? (
        <JsonViewer data={data} />
      ) : loading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: 280,
            gap: 12,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              border: "3px solid #e5e7eb",
              borderTopColor: "#3b82f6",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }}
          />
          <span style={{ color: "#9ca3af", fontSize: 13 }}>Running auction…</span>
        </div>
      ) : error ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: 280,
            gap: 8,
            padding: 24,
          }}
        >
          <span style={{ fontSize: 24 }}>⚠️</span>
          <span style={{ color: "#ef4444", fontSize: 13, textAlign: "center" }}>
            {error}
          </span>
          <span style={{ color: "#9ca3af", fontSize: 11, textAlign: "center" }}>
            Make sure API_BASE is set to your sandbox URL
          </span>
        </div>
      ) : empty ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: 280,
            gap: 8,
          }}
        >
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: "2px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#d1d5db",
              fontSize: 16,
            }}
          >
            ▶
          </span>
          <span style={{ color: "#d1d5db", fontSize: 13 }}>
            Run auction to see results
          </span>
        </div>
      ) : (
        <div
          style={{
            background: "#111827",
            height: "100%",
          }}
        >
          <JsonViewer data={data} />
        </div>
      )}
    </div>
  );
}

// ─── SIDEBAR ──────────────────────────────────────────────────────────────────
function Sidebar({ activeStepId, onSelect, completedSteps }) {
  return (
    <div
      style={{
        width: 240,
        minWidth: 240,
        background: "#eff6ff",
        borderRight: "1px solid #bfdbfe",
        height: "100vh",
        overflowY: "auto",
        position: "sticky",
        top: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Logo */}
      <div
        style={{
          padding: "0px 20px",
          height: 57,
          borderBottom: "1px solid #0f2744",
          background: "#1e3a5f",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <img
          src={koddiLogo}
          alt="Koddi"
          style={{ height: 24, objectFit: "contain", mixBlendMode: "screen" }}
        />
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: 1.5,
            color: "#93c5fd",
            textTransform: "uppercase",
            marginTop: 1,
          }}
        >
          API DEMO
        </span>
      </div>

      {/* Nav */}
      <nav style={{ padding: "8px 0", flex: 1 }}>
        {SECTIONS.map((section) => {
          const sectionDone = section.steps.filter((s) =>
            completedSteps.has(s.id)
          ).length;
          const sectionTotal = section.steps.length;
          return (
            <div key={section.id} style={{ marginBottom: 4 }}>
              <div
                style={{
                  padding: "8px 16px 4px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#9ca3af",
                    letterSpacing: 1,
                    textTransform: "uppercase",
                  }}
                >
                  {section.title}
                </span>
                <span style={{ fontSize: 10, color: "#d1d5db", fontWeight: 600 }}>
                  {sectionDone}/{sectionTotal}
                </span>
              </div>
              {section.steps.map((step) => {
                const isActive = step.id === activeStepId;
                const isDone = completedSteps.has(step.id);
                return (
                  <button
                    key={step.id}
                    onClick={() => onSelect(step.id)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      background: isActive ? "#dbeafe" : "none",
                      border: "none",
                      borderLeft: isActive
                        ? "3px solid #3b82f6"
                        : "3px solid transparent",
                      padding: "7px 16px 7px 13px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      transition: "all 0.1s",
                    }}
                  >
                    <span
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        background: isDone
                          ? "#16a34a"
                          : isActive
                          ? "#3b82f6"
                          : "#f3f4f6",
                        border: `2px solid ${
                          isDone ? "#16a34a" : isActive ? "#3b82f6" : "#e5e7eb"
                        }`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 9,
                        color: isDone ? "#fff" : isActive ? "#fff" : "#9ca3af",
                        fontWeight: 800,
                        flexShrink: 0,
                      }}
                    >
                      {isDone ? "✓" : step.id}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        color: isActive ? "#1d4ed8" : "#6b7280",
                        fontWeight: isActive ? 600 : 400,
                        lineHeight: 1.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {step.title}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>
    </div>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [activeStepId, setActiveStepId] = useState(ALL_STEPS[0].id);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  const activeStep = ALL_STEPS.find((s) => s.id === activeStepId);
  const globalIdx = stepIndex(activeStepId);

  function goTo(id) {
    setActiveStepId(id);
  }

  function goNext() {
    const next = ALL_STEPS[globalIdx + 1];
    if (next) {
      setCompletedSteps((prev) => new Set([...prev, activeStepId]));
      setActiveStepId(next.id);
    }
  }

  function goPrev() {
    const prev = ALL_STEPS[globalIdx - 1];
    if (prev) setActiveStepId(prev.id);
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'DM Sans', sans-serif; background: #fff; }
        @keyframes spin { to { transform: rotate(360deg); } }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
      `}</style>

      <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
        <Sidebar
          activeStepId={activeStepId}
          onSelect={goTo}
          completedSteps={completedSteps}
        />

        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Top bar */}
          <div
            style={{
              background: "#eff6ff",
              borderBottom: "1px solid #bfdbfe",
              padding: "0px 40px",
              height: 57,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <span style={{ color: "#9ca3af", fontSize: 12 }}>
              Step{" "}
              <span style={{ color: "#111827", fontWeight: 700 }}>
                {globalIdx + 1}
              </span>{" "}
              of{" "}
              <span style={{ color: "#111827", fontWeight: 700 }}>
                {TOTAL_STEPS}
              </span>
            </span>
          </div>

          {/* Main scroll area */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            <StepView key={activeStepId} step={activeStep} globalIndex={globalIdx} />
          </div>

          {/* Prev / Next */}
          <div
            style={{
              borderTop: "1px solid #e5e7eb",
              padding: "12px 40px",
              display: "flex",
              justifyContent: "space-between",
              background: "#fff",
            }}
          >
            <button
              onClick={goPrev}
              disabled={globalIdx === 0}
              style={{
                background: "#f3f4f6",
                border: "none",
                borderRadius: 6,
                padding: "8px 20px",
                fontSize: 13,
                fontWeight: 600,
                cursor: globalIdx === 0 ? "not-allowed" : "pointer",
                color: globalIdx === 0 ? "#d1d5db" : "#374151",
              }}
            >
              ← Prev
            </button>
            <button
              onClick={goNext}
              disabled={globalIdx === TOTAL_STEPS - 1}
              style={{
                background:
                  globalIdx === TOTAL_STEPS - 1 ? "#dbeafe" : "#3b82f6",
                border: "none",
                borderRadius: 6,
                padding: "8px 20px",
                fontSize: 13,
                fontWeight: 700,
                cursor:
                  globalIdx === TOTAL_STEPS - 1 ? "not-allowed" : "pointer",
                color: globalIdx === TOTAL_STEPS - 1 ? "#93c5fd" : "#fff",
              }}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
